/**
 * EditorialImage — PHASE 8 (BRAND_SYSTEM_WMGRA §6, motion signature)
 *
 * Image éditoriale publique avec l'unique effet « riche » autorisé par le
 * brand system : un voile navy qui se retire latéralement à l'entrée dans
 * le viewport (900ms, easing maison). Légende optionnelle type reportage.
 *
 * Props :
 *   src, alt        — image (WebP de /images/brand/)
 *   ratio           — aspect-ratio CSS ("3/2", "2/3", "1/1")
 *   caption         — légende reportage (string ou node)
 *   overline        — petit repère au-dessus de la légende (ex. numéral romain)
 *   focal           — point focal photographique ("65% 40%") — SPRINT 8 M6.
 *                     Vide = centre (object-position par défaut).
 *   delay           — délai du reveal (ms)
 */
import { useScrollReveal } from '../../hooks/useScrollReveal';
import { staticImageSrcSet } from '../../lib/imageOptimization';
import { ArrowsOut } from '@phosphor-icons/react';

export function EditorialImage({
  src,
  alt,
  ratio = '3/2',
  caption,
  overline,
  delay = 0,
  className = '',
  focal = '',
  testid,
  sizes = '(max-width: 768px) 100vw, 60vw',
  onOpen,
  openLabel,
}) {
  const [ref, visible] = useScrollReveal({ threshold: 0.2 });
  // HARDENING V1 (LOT 5) — variante 960px servie quand l'affichage le permet
  // (galerie ≤ 900 px affichés vs original 1920 px : ×3 d'octets évités).
  const srcSet = staticImageSrcSet(src);
  return (
    <figure ref={ref} className={className} data-testid={testid}>
      <div className="relative overflow-hidden" style={{ aspectRatio: ratio }}>
        {/* srcSet/sizes AVANT src : React applique les attributs dans l'ordre
            des props — src posé en premier déclencherait le fetch de
            l'original avant que le navigateur connaisse le srcset (double
            téléchargement constaté en mesure). */}
        <img
          srcSet={srcSet || undefined}
          sizes={srcSet ? sizes : undefined}
          src={src}
          alt={alt}
          loading="lazy"
          decoding="async"
          className="absolute inset-0 w-full h-full object-cover"
          style={focal ? { objectPosition: focal } : undefined}
        />
        <span
          aria-hidden
          className={`absolute inset-0 bg-navy-deep origin-right motion-reduce:hidden transition-transform [transition-duration:900ms] [transition-timing-function:cubic-bezier(0.22,1,0.36,1)] ${
            visible ? 'scale-x-0' : 'scale-x-100'
          }`}
          style={{ transitionDelay: visible ? `${delay}ms` : '0ms' }}
        />
        {onOpen && <button type="button" onClick={onOpen} aria-label={openLabel || alt}
          className="absolute inset-0 group cursor-zoom-in focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-4px] focus-visible:outline-gold">
          <span className="absolute bottom-4 right-4 w-11 h-11 flex items-center justify-center text-ivory bg-navy/70 border border-ivory/30 motion-safe:transition-transform motion-safe:group-hover:scale-110"><ArrowsOut size={20} aria-hidden /></span>
        </button>}
      </div>
      {(caption || overline) && (
        <figcaption className="mt-4 flex items-baseline gap-3">
          {overline && (
            <span className="font-heading italic text-gold-dark text-lg leading-none shrink-0">
              {overline}
            </span>
          )}
          {caption && (
            <span className="text-[13px] text-graphite leading-relaxed">{caption}</span>
          )}
        </figcaption>
      )}
    </figure>
  );
}

export default EditorialImage;
